import React from "react";
import { useAuth } from "@/contexts/AuthContext.jsx";

import messagingService, {
  MESSAGES_CHANGED_EVENT,
} from "@/services/messagingService.js";

function isFulfillmentConversation(conversation) {
  const description = String(conversation?.description || "").trim().toLowerCase();
  return description.startsWith("prescription_fulfillment:");
}

function sumUnreadConversations(conversations) {
  return (Array.isArray(conversations) ? conversations : []).reduce((total, conversation) => {
    const unreadCount = Number(conversation?.unreadCount || 0);
    return total + (Number.isFinite(unreadCount) && unreadCount > 0 ? unreadCount : 0);
  }, 0);
}

export default function useUnreadMessages(userId) {
  const { profile } = useAuth();
  const stableUserId = String(userId || "").trim();
  const currentRole = String(profile?.role || "").trim().toLowerCase();
  const [loading, setLoading] = React.useState(true);
  const [unreadCount, setUnreadCount] = React.useState(0);
  const [unreadConversations, setUnreadConversations] = React.useState(0);

  const load = React.useCallback(async () => {
    if (!stableUserId) {
      setUnreadCount(0);
      setUnreadConversations(0);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const conversations = await messagingService.getConversations();
      const visibleConversations =
        currentRole === "doctor"
          ? (Array.isArray(conversations) ? conversations : []).filter(
              (conversation) => !isFulfillmentConversation(conversation)
            )
          : conversations;
      const nextUnreadConversations = (Array.isArray(visibleConversations) ? visibleConversations : []).filter(
        (conversation) => Number(conversation?.unreadCount || 0) > 0
      ).length;

      setUnreadCount(sumUnreadConversations(visibleConversations));
      setUnreadConversations(nextUnreadConversations);
    } catch (error) {
      console.warn("Unread messages load failed:", error);
      setUnreadCount(0);
      setUnreadConversations(0);
    } finally {
      setLoading(false);
    }
  }, [currentRole, stableUserId]);

  React.useEffect(() => {
    void load();

    const onChanged = () => {
      void load();
    };
    const onFocus = () => {
      void load();
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        void load();
      }
    };

    const channel = messagingService.subscribeToConversations(stableUserId, onChanged);

    window.addEventListener(MESSAGES_CHANGED_EVENT, onChanged);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      messagingService.unsubscribe(channel);
      window.removeEventListener(MESSAGES_CHANGED_EVENT, onChanged);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [load, stableUserId]);

  return {
    loading,
    unreadCount,
    unreadConversations,
    refetch: load,
  };
}
